import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Crown, Plus, XCircle, Wrench, User, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import CustomerTabsBar from '../../components/CustomerTabsBar';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMemberships = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ customer_id: '', tier: 'basic', billing_period: 'annual' });
  const [loading, setLoading] = useState(false);
  // iter198 — post-grant "Add equipment now?" prompt.
  const [granted, setGranted] = useState(null); // { customer_id, customer_name, tier }

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/memberships`, { withCredentials: true }),
        axios.get(`${API}/customers`, { withCredentials: true }),
      ]);
      setMemberships(a.data);
      setCustomers(b.data);
    } catch (err) { console.error(err); }
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/memberships/grant`, form, { withCredentials: true });
      const cust = customers.find((c) => c.id === form.customer_id);
      toast.success('Membership granted');
      setShowForm(false);
      // iter198 — surface the Add Equipment prompt instead of just clearing.
      setGranted({ customer_id: form.customer_id, customer_name: cust?.name || 'Customer', tier: form.tier });
      setForm({ customer_id: '', tier: 'basic', billing_period: 'annual' });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  // iter198 — customer-scoped quick-action deep links.
  const goAddEquipment = (customerId) => navigate(`/admin/assets?customer_id=${customerId}&add=1`);
  const goViewEquipment = (customerId) => navigate(`/admin/assets?customer_id=${customerId}`);
  const goViewCustomer = (customerId) => navigate(`/admin/customers?open=${customerId}`);
  const goViewInvoices = (customerName) => navigate(`/admin/invoices?customer=${encodeURIComponent(customerName || '')}`);

  const handleCancel = async (customer_id) => {
    try {
      await axios.delete(`${API}/admin/memberships/${customer_id}`, { withCredentials: true });
      toast.success('Membership cancelled');
      refresh();
    } catch { toast.error('Failed'); }
  };

  const colorOf = (s) => s === 'active' ? 'text-green-500 bg-green-500/10' : 'text-red-400 bg-red-500/10';

  return (
    <div data-testid="admin-memberships-page" className="space-y-4">
      <CustomerTabsBar userRole={user?.role} />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Memberships</h2>
          <p className="text-[#A3A3A3]">Manage customer memberships</p>
        </div>
        <Button data-testid="admin-grant-membership" onClick={() => setShowForm(!showForm)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
          <Plus className="w-4 h-4 mr-2" /> Grant Membership
        </Button>
      </div>

      {showForm && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
          <form onSubmit={handleGrant} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-2 md:col-span-2">
              <Label className="text-white">Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({...form, customer_id: v})} required>
                <SelectTrigger data-testid="grant-customer" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id} className="text-white">{c.name} ({c.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Tier</Label>
              <Select value={form.tier} onValueChange={(v) => setForm({...form, tier: v})}>
                <SelectTrigger data-testid="grant-tier" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="basic" className="text-white">Basic</SelectItem>
                  <SelectItem value="protect" className="text-white">Protect</SelectItem>
                  <SelectItem value="premier" className="text-white">Premier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Billing</Label>
              <Select value={form.billing_period} onValueChange={(v) => setForm({...form, billing_period: v})}>
                <SelectTrigger data-testid="grant-billing" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="annual" className="text-white">Annual</SelectItem>
                  <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4 flex gap-2">
              <Button data-testid="grant-submit" type="submit" disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Grant</Button>
              <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {/* iter198 — Post-grant prompt: nudge office to build the equipment profile. */}
      {granted && (
        <div data-testid="post-grant-prompt" className="bg-[#D4AF37]/10 border border-[#D4AF37]/50 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-white font-medium">Membership granted for {granted.customer_name} — Add equipment now?</p>
            <p className="text-[#A3A3A3] text-xs mt-0.5">Build the plumbing/equipment profile so the {granted.tier?.toUpperCase()} plan isn't active with no records.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="post-grant-add-equipment" onClick={() => goAddEquipment(granted.customer_id)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              <Wrench className="w-4 h-4 mr-1" /> Add Equipment
            </Button>
            <Button data-testid="post-grant-view-customer" onClick={() => goViewCustomer(granted.customer_id)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">
              <User className="w-4 h-4 mr-1" /> View Customer
            </Button>
            <Button data-testid="post-grant-done" onClick={() => setGranted(null)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Done</Button>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        {memberships.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No memberships yet</div>
        ) : memberships.map((m) => (
          <div key={m.id} data-testid={`membership-row-${m.customer_id}`} className="bg-[#141414] border border-[#262626] rounded-md p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              <Crown className="w-6 h-6 text-[#D4AF37]" />
              <div>
                <p className="text-white font-medium">{m.customer_name}</p>
                <p className="text-[#D4AF37] text-sm">
                  {m.plan_name || m.tier?.toUpperCase()}
                  {(() => {
                    const price = Number(m.paid_fee || m.monthly_fee || 0);
                    const cadence = m.billing_cadence || m.billing_period || 'annual';
                    return price > 0 ? ` — $${price.toFixed(2)} / ${cadence === 'monthly' ? 'mo' : 'yr'}` : (m.complimentary ? ' — Complimentary' : '');
                  })()}
                </p>
                <p className="text-[#A3A3A3] text-xs">Since {new Date(m.start_date).toLocaleDateString()}{m.granted_by_admin ? ' • Admin granted' : ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-1 rounded text-xs ${colorOf(m.status)}`}>{m.status?.toUpperCase()}</span>
              <Button data-testid={`membership-add-equipment-${m.customer_id}`} onClick={() => goAddEquipment(m.customer_id)} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-8">
                <Wrench className="w-3.5 h-3.5 mr-1" /> Add Equipment
              </Button>
              <Button data-testid={`membership-view-customer-${m.customer_id}`} onClick={() => goViewCustomer(m.customer_id)} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-8">
                <User className="w-3.5 h-3.5 mr-1" /> Customer
              </Button>
              <Button data-testid={`membership-view-equipment-${m.customer_id}`} onClick={() => goViewEquipment(m.customer_id)} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-8">
                <Eye className="w-3.5 h-3.5 mr-1" /> Equipment
              </Button>
              <Button data-testid={`membership-view-invoices-${m.customer_id}`} onClick={() => goViewInvoices(m.customer_name)} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-8">
                <FileText className="w-3.5 h-3.5 mr-1" /> Invoices
              </Button>
              {m.status === 'active' && (
                <Button data-testid={`membership-cancel-${m.customer_id}`} onClick={() => handleCancel(m.customer_id)} variant="outline" size="sm" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white h-8">
                  <XCircle className="w-4 h-4 mr-1" /> Cancel
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminMemberships;
